import type { CanvasObject, CanvasObjectType, SectionCanvasObject } from "./canvas-object";

// セクション（領域コンテナ）のドメイン定数と包含判定ロジック。
// 親子関係は永続化せず、「セクション移動時にどのオブジェクトを一緒に動かすか」を
// ドラッグ開始時にこの判定で幾何計算する（サーバスキーマ・移動 op の変更が不要で、
// 既存のクライアント駆動グループ移動と整合するため）。

/** クリック配置時の既定サイズ（幅）。 */
export const SECTION_DEFAULT_W = 960;
/** クリック配置時の既定サイズ（高さ）。 */
export const SECTION_DEFAULT_H = 540;
/** リサイズ・ドラッグ配置時の最小サイズ（幅）。タイトルバーが潰れない下限として設定。 */
export const SECTION_MIN_W = 200;
/** リサイズ・ドラッグ配置時の最小サイズ（高さ）。 */
export const SECTION_MIN_H = 120;
/** fill 未設定時に描画へ使う既定の背景色（薄いグレー。塗りはレンダラ側で半透明化する）。 */
export const SECTION_DEFAULT_FILL = "#8993a4";

/** 作成時の既定タイトル。 */
export const SECTION_DEFAULT_TITLE = "セクション";

/**
 * セクション移動時に一緒に移動する内容物の id を返す。
 *
 * 判定規則（設計文書 §3-6）:
 * - comment-thread は保存 x/y をアンカー点とし、その点がセクション矩形に含まれれば対象にする。
 * - comment-thread 以外は AABB（x/y/w/h）がセクション矩形に**完全に含まれる**こと。
 *   端に重なっただけのオブジェクトを巻き込まず、視覚的な「中に入っている」と一致させる。
 * - connector は端点追従に委ねるため対象外。
 * - locked のオブジェクトは追従しない（ロック＝位置固定の意図を優先する）。
 * - セクション自身は対象外。ネストされた別セクションは完全包含なら追従する。
 *
 * 回転は考慮しない（セクションは回転非対応。内容物が回転していても保存 AABB は
 * x/y/w/h のまま
 * であり、既存のグループ移動・マーキーと同じ近似に揃える）。
 */
export function collectSectionContainedObjectIds(
  objects: Iterable<CanvasObject>,
  section: SectionCanvasObject
): string[] {
  const result: string[] = [];
  for (const object of objects) {
    if (object.id === section.id) continue;
    // connector は端点追従に委ねるため対象外。comment-thread（ピン）は、アンカー（x/y）が
    // 内側にあれば内容物として追従させる（判定規則は collectObjectsCarriedByMove 参照）。
    if (object.type === "connector") continue;
    if (object.locked === true) continue;
    if (isCarriedWithin(object, section)) {
      result.push(object.id);
    }
  }
  return result;
}

/**
 * オブジェクト移動時に「上に載っている」オブジェクトの id を返す（セクション挙動の全オブジェクトへの一般化）。
 *
 * セクションだけでなく任意のオブジェクトを移動する際、その矩形へ**完全に含まれ**、かつ
 * mover **より前面（描画順で後ろ＝上に載っている）** に描かれるオブジェクトを一緒に動かす。
 * これにより「付箋や図形の上にすっぽり収まったスタンプ等」が、背面の図形の移動に追従する。
 *
 * 判定規則:
 * - mover がセクションの場合は従来どおり {@link collectSectionContainedObjectIds} に委譲する
 *   （セクションは常に最背面帯に描かれ、完全包含した内容物は必ず前面のため、前面判定を加えても
 *   結果は変わらない。既存のセクション挙動・ネスト挙動を厳密に維持する）。
 * - mover がセクション以外の場合、comment-thread は保存 x/y のアンカー点が mover の矩形に
 *   含まれれば対象にする。専用オーバーレイで常に前面に描かれるため、保存描画順は比較しない。
 * - comment-thread 以外は、完全包含に加えて {@link compareCanvasObjectsForRender} で mover より
 *   前面に描画されるものだけを対象にする。少しでもはみ出したオブジェクト、および mover より
 *   背面（下）にあるオブジェクトは対象外（置いていかれる）。
 * - connector は端点追従に委ねるため対象外。
 * - locked のオブジェクトは追従しない（ロック＝位置固定の意図を優先する）。
 * - mover 自身は対象外。
 * - mover が comment-thread の場合は、他のオブジェクトを運ばない。
 *
 * 回転は考慮しない（保存 AABB は x/y/w/h のまま。既存のグループ移動・マーキー・セクションと同じ近似）。
 */
export function collectObjectsCarriedByMove(
  objects: Iterable<CanvasObject>,
  mover: CanvasObject
): string[] {
  // コメントピンは面を持たないアンカー点であり、他オブジェクトを運ぶ主体にはならない
  // （同座標の別ピンだけが点包含になり得るが、ピンがピンを運ぶ挙動は意図しない）。
  if (mover.type === "comment-thread") return [];
  if (mover.type === "section") {
    return collectSectionContainedObjectIds(objects, mover);
  }
  const result: string[] = [];
  for (const object of objects) {
    if (object.id === mover.id) continue;
    if (object.type === "connector") continue;
    if (object.locked === true) continue;
    // コメントピンは専用オーバーレイ層で常に全オブジェクトより前面に描かれるため、保存 zOrder に
    // よる前面判定を適用しない。適用すると「図形より先に作ったコメントは追従しない」という、
    // 画面から読み取れない基準（作成順）で追従の有無が変わってしまう。
    if (object.type !== "comment-thread" && compareCanvasObjectsForRender(mover, object) >= 0) {
      continue;
    }
    if (isCarriedWithin(object, mover)) {
      result.push(object.id);
    }
  }
  return result;
}

/**
 * 描画バンド。セクションは通常オブジェクトより常に背面に描画するため、
 * zOrder 比較の前段でバンド（0 = セクション帯 / 1 = 通常帯）を比較する。
 * Web レンダリング（サブレイヤ分離）とサムネイル / PDF（描画順）で同じ規則を共有する。
 */
export function canvasObjectRenderBand(type: CanvasObjectType): 0 | 1 {
  return type === "section" ? 0 : 1;
}

/**
 * 描画順の比較関数。バンド（セクション帯が先）→ zOrder → id（同値時の安定化）の順で比較する。
 */
export function compareCanvasObjectsForRender(a: CanvasObject, b: CanvasObject): number {
  const bandDiff = canvasObjectRenderBand(a.type) - canvasObjectRenderBand(b.type);
  if (bandDiff !== 0) return bandDiff;
  if (a.zOrder !== b.zOrder) return a.zOrder - b.zOrder;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

interface Aabb {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * point が outer の AABB の内側にあるか（境界一致は「内側」とみなす）。
 *
 * コメントピン（comment-thread）の追従判定に使う。ピンは保存 x/y をアンカー（配置クリック位置）
 * とし、w/h はビューポート固定表示のための名目値でしかないため、矩形どうしの isAabbContained では
 * 「右端・下端から名目サイズ分の帯にアンカーがあると内包扱いにならない」ずれが出る。判定を点で
 * 行うことで、web 側の名目サイズ定数から domain の規則を独立させる。
 */
export function isPointContainedInAabb(point: { x: number; y: number }, outer: Aabb): boolean {
  return (
    point.x >= outer.x &&
    point.y >= outer.y &&
    point.x <= outer.x + outer.w &&
    point.y <= outer.y + outer.h
  );
}

/** 追従判定の本体。comment-thread はアンカー点、それ以外は AABB の完全包含で判定する。 */
function isCarriedWithin(object: CanvasObject, container: CanvasObject): boolean {
  return object.type === "comment-thread"
    ? isPointContainedInAabb(object, container)
    : isAabbContained(object, container);
}

/**
 * セクションは回転非対応（設計判断 #8）。保存前にセクションの rotation を取り除く。
 * transform 経路（op-applier / canvas-object-service）では rotation を strip しているが、
 * create 経路では範囲チェックしかしないため、細工クライアントが `type: "section", rotation: 45`
 * を送ると回転値が永続化され、本体は無回転表示なのに選択枠・PDF・サムネイルだけ回転する
 * 不整合になり得る。これを防ぐため、create 経路（WS / REST / MCP の prepareObjectForCreate）で
 * この関数を通す。section 以外・rotation 未設定はそのまま返す。
 */
export function stripSectionRotation(object: CanvasObject): CanvasObject {
  if (object.type !== "section" || object.rotation === undefined) return object;
  const { rotation: _ignoredRotation, ...rest } = object;
  return rest;
}

/** inner の AABB が outer の AABB に完全に含まれるか（境界一致は「含まれる」とみなす）。 */
export function isAabbContained(inner: Aabb, outer: Aabb): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h
  );
}
